import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useEarnings, type EarningsPeriod } from "@/entities/earnings";
import { AccrualRow, RobotIncomeRow } from "@/entities/user-robot";
import { ModeSwitch } from "@/features/switch-mode";
import { ROUTES } from "@/shared/config";
import { Button, Panel, Segmented, type SegmentedOption } from "@/shared/ui";
import { EarningsCard } from "@/widgets/earnings-card";
import { EarningsStats } from "@/widgets/earnings-stats";
import { HomeAlert } from "@/widgets/home-alert";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

import { ChooseRobot } from "./ChooseRobot";
import { RiskControl } from "./RiskControl";
import { TrustNote } from "./TrustNote";

/**
 * Главная — экран заработка.
 *
 * Роут ОДИН, а веток четыре: `state` считает бэкенд, фронт его не
 * пересчитывает (правило ТЗ №5). Что различается:
 *
 *  - `first_entry` — карточка с нулями и блок выбора робота вместо панелей
 *    дохода: показывать пустые «доход по роботам» человеку без роботов значит
 *    обещать данные, которых у него неоткуда взяться;
 *  - `active` — экран целиком;
 *  - `loss_alert` добавляет баннер и блок «контроль риска»;
 *  - `profit_milestone` — баннер и заметку о том, что результат не гарантирован.
 *
 * Период календарный (`день / неделя / месяц / всё время`) и уходит в запрос:
 * окно задаёт не только длину ряда, но и то, с чем сравнивается дельта.
 */
const PERIODS: readonly EarningsPeriod[] = ["day", "week", "month", "all"];

export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);
  // Период живёт в состоянии экрана, а не в URL: это настройка просмотра, а не
  // адрес — возвращаться к «главной за сутки» по ссылке незачем.
  const [period, setPeriod] = useState<EarningsPeriod>("month");
  const { data, isPending, isError, refetch } = useEarnings(period);

  const periodOptions = useMemo<readonly SegmentedOption<EarningsPeriod>[]>(
    () => PERIODS.map((item) => ({ value: item, label: t(`earnings.period.${item}`) })),
    [t],
  );

  // Порядок и высоты — как на собранном экране: шапка, переключатель счёта,
  // период, карточка дохода, полоса статистики. Скелетон, не совпадающий с
  // раскладкой, даёт скачок контента ровно в момент загрузки.
  if (isPending) return <ScreenSkeleton blocks={[24, 42, 42, 170, 107]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{
          label: t("states.error.action"),
          onClick: () => {
            void refetch();
          },
        }}
      />
    );
  }

  const isFirstEntry = data.state === "first_entry";

  return (
    // Каскад на всю главную, а не на отдельные блоки: это первый экран
    // приложения, и он должен собираться сверху вниз одним движением, а не
    // возникать целиком (FE-13). Вложенных каскадов внутри намеренно нет —
    // два наложенных ритма читаются как рассинхрон.
    <div className="stagger-children flex flex-col gap-3 px-[var(--layout-gutter)] py-3">
      <TopBar
        title={t("earnings.title")}
        subtitle={t(isFirstEntry ? "home.greetingSub" : "earnings.subtitle")}
      />

      {/* Какой счёт открыт — видно сразу, не уходя в профиль (доработки §3).
          `outline`, потому что строкой ниже стоит залитый период: два
          одинаково громких ряда табов подряд спорили бы за внимание, а этот
          не управляет главной цифрой — он говорит, чья она. */}
      <ModeSwitch variant="outline" />

      <Segmented
        options={periodOptions}
        value={period}
        onChange={setPeriod}
        ariaLabel={t("earnings.periodLabel")}
        variant="solid"
      />

      {data.alert ? <HomeAlert alert={data.alert} /> : null}

      <EarningsCard
        eyebrow={t(`earnings.eyebrow.${period}`)}
        total={data.total}
        deltaPct={data.delta_pct}
        deltaCaption={t(`earnings.deltaCaption.${period}`)}
        chart={data.chart}
        labels={data.chart_labels}
        hidden={hidden}
        onToggleHidden={() => {
          setHidden((value) => !value);
        }}
      />

      {isFirstEntry ? (
        <ChooseRobot
          onChoose={() => {
            void navigate(ROUTES.robots);
          }}
        />
      ) : (
        <>
          <EarningsStats stats={data.stats} />

          {data.robots.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h2 className="text-label font-bold text-text-bright">{t("earnings.byRobot")}</h2>
              <Panel tone="green" radius="md" className="px-2.5 py-1">
                {data.robots.map((item) => (
                  <RobotIncomeRow key={item.user_robot_id} item={item} />
                ))}
              </Panel>
            </section>
          ) : (
            <Button size="cta" block onClick={() => void navigate(ROUTES.robots)}>
              {t("home.chooseRobot.cta")}
            </Button>
          )}

          {data.recent.length > 0 ? (
            <section className="flex flex-col gap-2">
              <h2 className="text-label font-bold text-text-bright">{t("earnings.recent")}</h2>
              <Panel tone="green" radius="md" className="px-2.5 py-1">
                {data.recent.map((item) => (
                  <AccrualRow key={`${String(item.user_robot_id)}-${item.at}`} item={item} />
                ))}
              </Panel>
            </section>
          ) : null}

          {data.state === "loss_alert" ? <RiskControl /> : null}
          {data.state === "profit_milestone" ? <TrustNote /> : null}
        </>
      )}
    </div>
  );
}
